/**
 * InviteTeamCard — Property Manager-side quick-invite panel for spinning up
 * additional Demo Residents inside an isolated sandbox. Renders a shareable
 * `/register?role=tenant_resident&property=<slug>` link the PM can text or
 * email to their team so multiple people can log in as residents of the
 * same demo property. Milestone alerts, live pickup tracking, and QR check-
 * ins all flow inside the isolated demo_org_id so nothing bleeds into
 * production.
 *
 * Rendered on PropertyManagerDashboard (the demo PM sees it prominently);
 * intentionally low-emphasis for production PMs, since Site Staff Users
 * tab is the real onboarding surface for them.
 */
import { useMemo, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Copy, Users, Send, MessageSquare, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InviteTeamCard({ property, isDemo = false }) {
  const [copying, setCopying] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [sending, setSending] = useState(false);

  const link = useMemo(() => {
    if (!property?.property_id) return '';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/register?role=tenant_resident&property=${encodeURIComponent(property.property_id)}`;
  }, [property?.property_id]);

  if (!link) return null;

  const copyLink = async () => {
    setCopying(true);
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Invite link copied — paste it into your text or email.');
    } catch (_) {
      toast.error('Could not copy — long-press the link below to copy it manually.');
    } finally {
      setCopying(false);
    }
  };

  const sendInvite = async (channel) => {
    if (!inviteEmail && channel === 'email') {
      toast.error('Enter an email to send the invite.');
      return;
    }
    if (!inviteEmail && channel === 'sms') {
      toast.error('Enter a phone number to text the invite.');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/pm/invite-resident`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          property_id: property.property_id,
          name: inviteName || null,
          contact: inviteEmail,
          channel,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Failed to send invite');
        return;
      }
      toast.success(`Invite sent via ${channel === 'sms' ? 'SMS' : 'email'} to ${inviteEmail}`);
      setInviteEmail('');
      setInviteName('');
    } catch (_) {
      toast.error('Network error');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card
      className="p-5 border"
      style={{ backgroundColor: '#000', borderColor: '#C9A962' }}
      data-testid="invite-team-card"
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5" style={{ color: '#C9A962' }} />
          <h3
            className="text-lg font-bold"
            style={{
              fontFamily: 'Barlow Condensed, sans-serif',
              color: '#C9A962',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            {isDemo ? 'Invite Your Team to This Demo' : 'Invite New Residents'}
          </h3>
        </div>
        {isDemo && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#C9A962]">
            <Sparkles className="h-3 w-3" /> Demo Sandbox
          </span>
        )}
      </div>

      <p className="text-xs text-zinc-300 mb-3 leading-snug">
        {isDemo
          ? 'Share this link with your team so they can sign up as Residents inside your isolated demo. Milestone alerts + live pickup tracking will update in real time for everyone signed in.'
          : 'Share this link with new residents to auto-attach them to this property. They pick their unit and set a password.'}
      </p>

      <div className="flex gap-2 mb-4">
        <Input
          value={link}
          readOnly
          className="bg-zinc-900 border-zinc-700 text-zinc-200 text-xs font-mono"
          data-testid="invite-team-link"
          onClick={(e) => e.target.select()}
        />
        <Button
          onClick={copyLink}
          disabled={copying}
          className="bg-[#C9A962] hover:bg-[#b89651] text-black shrink-0"
          data-testid="invite-team-copy-btn"
        >
          <Copy className="h-4 w-4 mr-1" /> Copy
        </Button>
      </div>

      <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3 space-y-2">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500">
          Or send directly (optional)
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Input
            placeholder="Team member name (optional)"
            value={inviteName}
            onChange={(e) => setInviteName(e.target.value)}
            className="bg-zinc-900 border-zinc-700 text-white text-sm"
            data-testid="invite-team-name-input"
          />
          <Input
            placeholder="Email or +1 phone"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            className="bg-zinc-900 border-zinc-700 text-white text-sm"
            data-testid="invite-team-contact-input"
          />
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => sendInvite('email')}
            disabled={sending || !inviteEmail}
            className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs flex-1 h-8"
            data-testid="invite-team-send-email-btn"
          >
            <Send className="h-3 w-3 mr-1" /> Email
          </Button>
          <Button
            onClick={() => sendInvite('sms')}
            disabled={sending || !inviteEmail}
            className="bg-blue-600 hover:bg-blue-500 text-white text-xs flex-1 h-8"
            data-testid="invite-team-send-sms-btn"
          >
            <MessageSquare className="h-3 w-3 mr-1" /> SMS
          </Button>
        </div>
      </div>
    </Card>
  );
}
